<template>
  <div class="tenancy-index-adjustment">
    <hl-Modal
      v-if="showCancelCPIModal"
      iconClass="budicon-alert-sign text-warning"
      title="Er du sikker?"
      class="text-center"
    >
      <div>
        <p>
          Dette vil stoppe indeksreguleringen på leieforholdet. Vi sender også en epost til leietaker for å informere om
          dette.
        </p>
        <div class="mt-5">
          <hl-Button variant="secondary" class="mx-2" outline @click="showCancelCPIModal = false">Avbryt</hl-Button>
          <hl-Button variant="warning" class="mx-2" @click="cancelCPI">Stopp indeksreguleringen</hl-Button>
        </div>
      </div>
    </hl-Modal>

    <SubscriptionModal
      :show="shouldShowSubscriptionModal"
      :currentPlanName="subscriptionPlanName"
      :plans="plans ? [plans.find(plan => plan.name === 'Premium')] : []"
      :shouldShowCPIAdjustmentDetails="['', 'Basic'].includes(subscriptionPlanName)"
      :loading="isUpdatingSubscription"
      @input="handleSubscriptionChange"
      @close="shouldShowSubscriptionModal = false"
    />

    <BlockContainer class="tenancy-index-adjustment__wrapper p-4">
      <div class="row align-items-center">
        <div class="col">
          <h5>Automatisk indeksregulering</h5>
        </div>

        <div v-if="!(loading || saving)" class="col edit-buttons text-right">
          <EditButton v-if="!CPIAdjustment.inProgress" :isActive="editMode" @click.native="beginEditing" />
        </div>
      </div>

      <div v-if="loading || saving" class="spinner-grow spinner-grow-sm d-inline-block ml-3 mt-5" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <div v-else>
        <div v-if="canNoLongerCancelCPIAdjustment" class="mt-4">
          <hl-Alert variant="info">
            Husleien for denne måneden har allerede blitt indeksregulert og fakturert, og du kan ikke skru av
            indeksreguleringen. Ønsker du likevel ikke å indeksregulere kan du gå innpå
            <router-link
              :to="{
                name: 'tenancy_payments',
                params: {
                  landlordId: this.landlordId,
                  tenancyId: this.tenancyId,
                },
              }"
              >betalinger</router-link
            >
            og kreditere deler av fakturaen.
          </hl-Alert>
        </div>

        <div v-if="canStillCancelCPIAdjustment" class="mt-4">
          <hl-Alert variant="info">
            Husleien for denne måneden skal snart indeksreguleres. Vil du stoppe indeksreguleringen?
            <div class="mt-2">
              <hl-Button variant="light" class="text-white" outline size="sm" @click="showCancelCPIModal = true"
                >Stopp indeksreguleringen</hl-Button
              >
            </div>
          </hl-Alert>
        </div>

        <p v-if="isActive" class="mt-4">
          Indeksregulering er aktivert.
          <br />
          <span v-if="!CPIAdjustment.inProgress">For å deaktivere, klikk på “Endre”-ikonet.</span>
        </p>
        <p v-else class="mt-4">
          Indeksregulering er ikke aktivert.
          <br />
          <span v-if="!CPIAdjustment.inProgress">Klikk på “Endre”-ikonet for å aktivere indeksregulering.</span>
        </p>
        <hl-Switch
          v-if="editMode"
          class="mt-2 d-inline-block"
          :class="{ 'mb-3': !localIsActive }"
          showState
          onState="Aktivert"
          offState="Ikke aktivert"
          v-model="localIsActive"
        />
        <h6 v-else class="mt-4">
          <i class="budicon-solid" :class="CPIStatusIconClass"></i>
          <h6 class="d-inline-block ml-3">{{ isActive ? 'Aktivert' : 'Ikke aktivert' }}</h6>
        </h6>
        <div v-if="editMode">
          <template v-if="localIsActive">
            <h6 class="mt-4">Neste indeksregulering</h6>
            <hl-Select
              v-if="localIsActive"
              placeholder="Ikke satt"
              placeholderValue="__none__"
              :options="CPIAdjustmentDateOptions"
              :disabled="isCPIDateOptionsDisabled"
              v-model="nextCPIAdjustment"
            />
          </template>
          <hl-Button @click="handleCPIAdjustment"> Lagre </hl-Button>
        </div>
        <hl-Date
          v-else-if="CPIAdjustment.status === 'active' && estimatedNextCPIAdjustment"
          :date="estimatedNextCPIAdjustment"
        />
      </div>
      <div class="mt-4">
        <i class="budicon budicon-document-page mr-2"></i>
        <a href="https://www.husleie.no/ressurser/artikler-om-boligutleie/okning-av-husleien" target="_blank">
          Les mer om når du kan justere husleien
        </a>
      </div>
    </BlockContainer>
  </div>
</template>

<script>
import BlockContainer from '@/components/BlockContainer';
import CancelButton from '@/components/UI/buttons/CancelButton';
import EditButton from '@/components/UI/buttons/EditButton';
import SaveButton from '@/components/UI/buttons/SaveButton';
import { createCPIAdjustmentDateOptions } from '../../../lib/CPIHelpers';
import Modal from '@modules/husleie-framework/src/components/modals/Modal.vue';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import { mapGetters, mapActions, mapState } from 'vuex';
import moment from 'moment';
import Alert from '@modules/husleie-framework/src/components/text/Alert.vue';
import Switch from '@modules/husleie-framework/src/components/forms/inputs/switch/Switch.vue';
import Select from '@modules/husleie-framework/src/components/forms/inputs/select/Select.vue';
import Date from '@modules/husleie-framework/src/components/time/Date.vue';
import SubscriptionModal from '@/components/detailedRentalObject/SubscriptionModal.vue';
import i18nCommon from '@modules/i18n/mixins/Common';
import subscription from '@/mixins/tenancy/subscription';
import { TenancyApi } from '@modules/husleieapi2';

export default {
  mixins: [i18nCommon, subscription],
  components: {
    BlockContainer,
    CancelButton,
    EditButton,
    SaveButton,
    SubscriptionModal,
    'hl-Modal': Modal,
    'hl-Button': Button,
    'hl-Alert': Alert,
    'hl-Switch': Switch,
    'hl-Select': Select,
    'hl-Date': Date,
  },

  props: {
    tenancyId: [String, Number],
    landlordId: [String, Number],
  },

  data() {
    return {
      editMode: false,
      loading: false,
      saving: false,
      localCopy: {
        status: 'inactive',
      },
      nextCPIAdjustment: null,
      showCancelCPIModal: false,
      plans: [],
      shouldShowSubscriptionModal: false,
      isUpdatingSubscription: false,
    };
  },

  computed: {
    ...mapGetters('tenancy', ['getTenancy']),
    ...mapGetters(['getLandlordProfileMeta']),
    ...mapState('tenancy', {
      CPIAdjustment: state => state.CPIAdjustment,
    }),

    localIsActive: {
      get() {
        return this.localCopy.status === 'active';
      },
      set(v) {
        if (v) {
          this.localCopy.status = 'active';
        } else {
          this.localCopy.status = 'inactive';
        }
      },
    },

    isActive() {
      return this.CPIAdjustment.status === 'active';
    },

    estimatedNextCPIAdjustment() {
      if (this.getTenancy == null || this.getTenancy.rentAdjustment == null) return;
      return moment(this.CPIAdjustment.previousCPIAdjustment).add(1, 'years').toDate();
    },

    previousCPIAdjustment() {
      if (this.nextCPIAdjustment === '__none__') return;
      return moment(this.nextCPIAdjustment).subtract(1, 'year').format('YYYY-MM-DD');
    },

    CPIStatusIconClass() {
      if (this.isActive) return 'budicon-solid-tick-sign text-primary';
      return 'budicon-solid-cross-sign text-secondary';
    },

    CPIAdjustmentDateOptions() {
      const dueDay = parseInt(this.getTenancy.rent.dueDay);
      const tenancyStartDate = this.getTenancy.startDate;

      return createCPIAdjustmentDateOptions(tenancyStartDate, dueDay, moment().format('YYYY-MM-DD')).map(value => ({
        name: moment(value, 'YYYY-MM-DD').format('YYYY MMMM'),
        value,
      }));
    },

    isLastCPIInTheFuture() {
      if (this.CPIAdjustment == null || this.CPIAdjustment.previousCPIAdjustment == null) return;
      const today = moment();
      return moment(this.CPIAdjustment.previousCPIAdjustment).isSameOrAfter(today);
    },

    canStillCancelCPIAdjustment() {
      return this.CPIAdjustment.inProgress && !this.isLastCPIInTheFuture;
    },

    canNoLongerCancelCPIAdjustment() {
      return this.CPIAdjustment.inProgress && this.isLastCPIInTheFuture;
    },

    canSave() {
      // If status is active, user must select a date
      if (this.localCopy.status === 'active' && this.nextCPIAdjustment === '__none__') return false;
      // If the user hasn't changed anything, he shouldn't be able to save
      if (
        this.localCopy.status === this.CPIAdjustment.status &&
        this.previousCPIAdjustment === this.CPIAdjustment.previousCPIAdjustment
      )
        return false;
      return true;
    },

    initData() {
      return {
        landlordId: this.landlordId,
        tenancyId: this.tenancyId,
      };
    },

    subscriptionPlanName() {
      return this.getTenancy.subscription.subscriptionPlanName || '';
    },

    isProfessionalLandlord() {
      const landlord = this.getLandlordProfileMeta(this.landlordId);

      return landlord && landlord.meta.isProfessional;
    },

    isCPIDateOptionsDisabled() {
      if (
        this.subscriptionPlanName === 'Premium' ||
        (this.isProfessionalLandlord && this.subscriptionPlanName === 'Basic')
      ) {
        return false;
      }
      return true;
    },
  },

  watch: {
    initData: {
      handler: 'init',
      immediate: true,
    },
  },

  methods: {
    ...mapActions('tenancy', [
      'fetchTenancyCPIAdjustment',
      'updateTenancyCPIAdjustment',
      'cancelTenancyCPIAdjustment',
      'fetchTenancy',
    ]),

    async init(payload) {
      this.loading = true;
      try {
        await this.fetchTenancyCPIAdjustment(payload);
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke hente indeksregulering.', 'error');
      }
      this.loading = false;
    },

    beginEditing() {
      if (this.editMode) {
        this.editMode = false;
        return;
      }

      if (
        !this.isProfessionalLandlord &&
        ['', 'Basic'].includes(this.subscriptionPlanName) &&
        this.CPIAdjustment.status !== 'active'
      ) {
        this.shouldShowSubscriptionModal = true;
        return;
      }

      this.editMode = true;
      this.localCopy = JSON.parse(JSON.stringify(this.CPIAdjustment));
      // If already active, we try to select the option already set.
      if (this.CPIAdjustment.status === 'active') {
        const estimatedNextDate = moment(this.estimatedNextCPIAdjustment).format('YYYY-MM');
        const selectOption = this.CPIAdjustmentDateOptions.find(
          // Check if same year and month by comparing 7 first
          // characters of ISO date 'YYYY-MM'
          ({ value }) => value.substr(0, 7) === estimatedNextDate
        );
        this.nextCPIAdjustment = selectOption ? selectOption.value : '__none__';
      } else {
        this.nextCPIAdjustment = this.CPIAdjustmentDateOptions.length ? this.CPIAdjustmentDateOptions[0].value : null;
      }
    },

    async save() {
      this.saving = true;
      try {
        await this.updateTenancyCPIAdjustment({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          data: {
            status: this.localCopy.status,
            previousCPIAdjustment: this.previousCPIAdjustment,
          },
        });
        this.editMode = false;
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke lagre indeksregulering.', 'error');
      }
      this.saving = false;
    },

    async cancelCPI() {
      try {
        this.cancelTenancyCPIAdjustment({ landlordId: this.landlordId, tenancyId: this.tenancyId });
        this.$toast('Indeksreguleringen stoppet', 'success');
        this.showCancelCPIModal = false;
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke stoppe indeksreguleringen.', 'error');
      }
    },

    async fetchAvailablePlans() {
      try {
        const api = new TenancyApi();
        this.plans = await api.getLandlordTenancyAvailableSubscriptions(this.landlordId, this.tenancyId);
      } catch (error) {
        throw new Error('getLandlordTenancyAvailableSubscriptions: ', error);
      }
    },

    async updateTenancyStore() {
      try {
        this.fetchTenancy({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
        });
      } catch (error) {
        throw new Error('Failed to fetch tenancy', error);
      }
    },

    async handleSubscriptionChange(newPlanName) {
      this.isUpdatingSubscription = true;

      try {
        await this.changeSubscriptionPlan(
          this.landlordId,
          this.tenancyId,
          this.plans,
          this.subscriptionPlanName,
          newPlanName,
          'tenancyCPIAdjustment'
        );
        this.$toast(
          this.translate('subscription.alert.changed subscription to {planName}', { planName: newPlanName }),
          'success'
        );
        await this.updateTenancyStore();
      } catch {
        this.$toast(
          this.translate('subscription.alert.change of subscription to {planName} failed', { planName: newPlanName }),
          'error'
        );
      }
      this.shouldShowSubscriptionModal = this.isUpdatingSubscription = this.editMode = false;
    },

    async handleCPIAdjustment() {
      await this.save();
      this.editMode = false;
    },
  },

  created() {
    this.fetchAvailablePlans();
  },
};
</script>
<style>
.tenancy-index-adjustment,
.tenancy-index-adjustment .tenancy-index-adjustment__wrapper {
  @apply h-full;
}
</style>
